import { apiFetchJson } from '@wundo/client/api.ts';
import type { ReviewFinding, ReviewSeverity } from '@/lib/conversationReviews.ts';

/*
 * Everything the backend knows about one conversation, in one answer: who it
 * belongs to, what it is made of, what the orchestrator was doing, and what the
 * analyser made of it. Read by the inspector on the messages page and by
 * nothing else.
 */

export interface ConversationDebugUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  organisation_id: string | null;
}

export interface ConversationDebugConversation {
  id: string;
  agent: string;
  channel: string;
  type: 'private' | 'shared';
  user_id: string;
  awaiting_action: boolean | null;
  active_skill_id: string | null;
  origin_recommendation_id: string | null;
  origin_schedule_id: string | null;
  agent_context: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationDebugStats {
  total: number;
  by_type: Record<string, number>;
  first_message_at: string | null;
  last_message_at: string | null;
  duration_ms: number | null;
  content_filtered_count: number;
  failed_delivery_count: number;
  distinct_languages: string[];
  distinct_skill_ids: string[];
}

export interface ConversationDebugRoleplay {
  id: string;
  status: string;
  mode: string | null;
  scenario: {
    situation: string;
    persona: { name: string; role: string; behavior: string };
    learning_objectives: string[];
  };
  source_conversation_id: string | null;
  debrief: {
    /** False when the user barely took part and nothing was rated; absent on older debriefs. */
    took_part?: boolean;
    recap: string;
    ratings: { objective: string; score: number; feedback: string }[];
    standout: string;
    tip: string;
  } | null;
  expires_at: string;
  completed_at: string | null;
  created_at: string;
}

export interface ConversationDebugReview {
  id: string;
  chat_session_id: string;
  severity: ReviewSeverity | null;
  findings: ReviewFinding[];
  trace_id: string | null;
  span_id: string | null;
}

export interface ConversationDebugChatSession {
  id: string;
  conversation_id: string;
  title: string | null;
  summary: string | null;
  first_message_id: number;
  last_message_id: number;
  message_count: number;
  analyzers_completed: string[];
  fully_analyzed: boolean;
  started_at: string | null;
  created_at: string;
}

export interface ConversationDebugSessionEntry {
  session: ConversationDebugChatSession;
  review: ConversationDebugReview | null;
}

export interface ConversationDebugPayload {
  conversation: ConversationDebugConversation;
  is_current: boolean;
  user: ConversationDebugUser;
  organisation: { id: string; name: string } | null;
  active_skill: { id: string; title: string | null } | null;
  origin_recommendation: { id: string } | null;
  origin_schedule: { id: string } | null;
  roleplay_session: ConversationDebugRoleplay | null;
  stats: ConversationDebugStats;
  chat_sessions: {
    items: ConversationDebugSessionEntry[];
    total: number;
    limit: number;
    offset: number;
  };
}

/** How many analysed sessions the inspector shows at a time. */
export const DEBUG_SESSIONS_PAGE_SIZE = 20;

export interface ConversationDebugOptions {
  sessionsLimit?: number;
  sessionsOffset?: number;
}

export function fetchConversationDebug(
  conversationId: string,
  options: ConversationDebugOptions = {},
): Promise<ConversationDebugPayload> {
  const params = new URLSearchParams();
  if (options.sessionsLimit !== undefined) {
    params.set('sessions_limit', String(options.sessionsLimit));
  }
  if (options.sessionsOffset !== undefined) {
    params.set('sessions_offset', String(options.sessionsOffset));
  }
  const query = params.toString();

  return apiFetchJson<{ data: ConversationDebugPayload }>(
    `/conversations/${conversationId}/debug${query ? `?${query}` : ''}`,
  ).then((response) => response.data);
}
